import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_LINKS } from '../mock';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goToSection = (href) => {
    const id = href.replace('#', '');
    if (id === 'home') {
      if (onHome) window.scrollTo({ top: 0, behavior: 'smooth' });
      else window.location.href = '/';
      return;
    }
    if (onHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Full navigation to homepage with hash; Landing scrolls to it on load.
      window.location.href = `/#${id}`;
    }
  };

  const handleClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    goToSection(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'py-2' : 'py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5">
        <div
          className={`flex items-center justify-between rounded-2xl px-4 md:px-6 py-3 transition-all duration-300 ${
            scrolled || !onHome
              ? 'neon-border bg-[#0f1c30]/80 backdrop-blur-xl border-white/10'
              : 'bg-transparent border-transparent'
          }`}
        >
          <a href="#home" onClick={(e) => handleClick(e, '#home')} className="flex items-center gap-2 group">
            <img src="/brand/logo.png" alt="Electronico" className="h-14 w-14 object-contain" />
            <span className="font-display text-lg md:text-xl font-bold tracking-tight">
              Electronico
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            <a
              href="#home"
              onClick={(e) => handleClick(e, '#home')}
              className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white rounded-lg transition-colors relative group"
            >
              Home
              <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-gradient-to-r from-[#0ef3fe] to-[#088ffe] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
            {NAV_LINKS.map((l) => (
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white rounded-lg transition-colors relative group"
                >
                  {l.label}
                  <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-gradient-to-r from-[#0ef3fe] to-[#088ffe] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => handleClick(e, l.href)}
                  className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white rounded-lg transition-colors relative group"
                >
                  {l.label}
                  <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-gradient-to-r from-[#0ef3fe] to-[#088ffe] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </a>
              )
            ))}
          </nav>

          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-2 rounded-xl bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-[#0ef3fe] transition-colors"
          >
            Start a Project
          </Link>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden mt-2 neon-border rounded-2xl bg-[#0f1c30]/95 backdrop-blur-xl p-4 flex flex-col gap-1">
            <a
              href="#home"
              onClick={(e) => handleClick(e, '#home')}
              className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg"
            >
              Home
            </a>
            {NAV_LINKS.map((l) => (
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => handleClick(e, l.href)}
                  className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  {l.label}
                </a>
              )
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 text-center rounded-xl bg-white text-black px-5 py-3 font-semibold"
            >
              Start a Project
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
